import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { OrganizationRole, PermissionKey } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";

type Membro = { id: string; role: string; customRole?: { id: string; label: string } | null };

/** "role:DENTIST" ou "custom:<id>" — o mesmo formato do seletor de cargo da lista. */
export type ValorDoAcesso = string;

export const valorDoMembro = (m: Membro): ValorDoAcesso =>
  m.customRole ? `custom:${m.customRole.id}` : `role:${m.role}`;

const paraInput = (v: ValorDoAcesso) =>
  v.startsWith("custom:")
    ? { customRoleId: v.slice("custom:".length) }
    : { role: v.slice("role:".length) as Exclude<OrganizationRole, "OWNER" | "PARTNER"> };

/**
 * O rascunho do acesso de um colaborador: qual acesso está escolhido e que chaves foram mexidas
 * por cima das permissões dele. Nada grava até um dos três gestos da barra — aplicar o acesso,
 * salvar como acesso novo ou atualizar o acesso escolhido.
 */
export function useAcessoDoMembro(membro: Membro | null, aberto: boolean) {
  const utils = trpc.useUtils();
  const atual = membro ? valorDoMembro(membro) : "";
  const [escolhido, setEscolhido] = useState<ValorDoAcesso>(atual);
  const [mexidas, setMexidas] = useState<Partial<Record<PermissionKey, boolean>>>({});

  useEffect(() => {
    setEscolhido(atual);
    setMexidas({});
  }, [atual, membro?.id]);

  const ehDono = membro?.role === "OWNER";
  // Sócio não tem grade: acesso total, como o proprietário.
  const acessoTotal = escolhido === "role:PARTNER";
  const permissoesQuery = trpc.organization.permissions.doAcesso.useQuery(paraInput(escolhido || "role:VIEWER"), {
    enabled: aberto && !!membro && !ehDono && !acessoTotal && !!escolhido,
  });
  const base = useMemo(
    () => Object.fromEntries((permissoesQuery.data ?? []).map((p) => [p.key, p.granted])) as Record<PermissionKey, boolean>,
    [permissoesQuery.data],
  );

  const efetiva = (k: PermissionKey) => mexidas[k] ?? base[k] ?? false;
  const mudancas = (Object.keys(mexidas) as PermissionKey[])
    .filter((k) => mexidas[k] !== base[k])
    .map((k) => ({ permissionKey: k, granted: mexidas[k]! }));
  const mudouAcesso = !!membro && escolhido !== atual;

  const escolher = (v: ValorDoAcesso) => {
    setEscolhido(v);
    setMexidas({});
  };
  const alternar = (k: PermissionKey) => setMexidas((m) => ({ ...m, [k]: !efetiva(k) }));
  const descartar = () => {
    setEscolhido(atual);
    setMexidas({});
  };

  const invalidar = () => {
    utils.team.list.invalidate();
    utils.team.getById.invalidate();
    utils.me.current.invalidate();
    utils.organization.customRoles.list.invalidate();
    utils.organization.permissions.list.invalidate();
    utils.organization.permissions.doAcesso.invalidate();
  };
  const erro = (e: { message: string }) => toast.error(e.message || "Não foi possível salvar.");
  const opcoes = { onError: erro };

  const updateRole = trpc.team.updateRole.useMutation(opcoes);
  const assignCustomRole = trpc.team.assignCustomRole.useMutation(opcoes);
  const setMany = trpc.organization.permissions.setMany.useMutation(opcoes);
  const setPermissions = trpc.organization.customRoles.setPermissions.useMutation(opcoes);
  const criar = trpc.organization.customRoles.create.useMutation(opcoes);

  async function atribuir(v: ValorDoAcesso) {
    if (!membro) return;
    if (v.startsWith("custom:")) {
      await assignCustomRole.mutateAsync({ membershipId: membro.id, customRoleId: v.slice("custom:".length) });
    } else {
      await updateRole.mutateAsync({ membershipId: membro.id, role: paraInput(v).role! });
    }
  }

  async function concluir(msg: string, acao: () => Promise<unknown>) {
    try {
      await acao();
      invalidar();
      setMexidas({});
      toast.success(msg);
    } catch {
      // o toast de erro já saiu pelo onError da mutation
    }
  }

  const aplicarAcesso = () => concluir("Acesso alterado.", () => atribuir(escolhido));

  const atualizarAcesso = () =>
    concluir("Acesso atualizado.", async () => {
      const alvo = paraInput(escolhido);
      if (alvo.customRoleId) await setPermissions.mutateAsync({ customRoleId: alvo.customRoleId, changes: mudancas });
      else await setMany.mutateAsync({ role: alvo.role!, changes: mudancas });
      if (mudouAcesso) await atribuir(escolhido);
    });

  /** O acesso novo nasce do papel-base do escolhido, com TODAS as chaves como estão na tela. */
  const salvarComoNovo = (label: string, papelBase: Exclude<OrganizationRole, "OWNER" | "PARTNER">) =>
    concluir(`Acesso "${label}" criado e atribuído.`, () =>
      criar.mutateAsync({
        label,
        basedOnRole: papelBase,
        permissions: (Object.keys(base) as PermissionKey[]).map((k) => ({ permissionKey: k, granted: efetiva(k) })),
        assignToMembershipId: membro!.id,
      }),
    );

  const salvando =
    updateRole.isPending || assignCustomRole.isPending || setMany.isPending || setPermissions.isPending || criar.isPending;

  return {
    ehDono,
    acessoTotal,
    escolhido,
    escolher,
    efetiva,
    alternar,
    mexidas,
    mudancas,
    mudouAcesso,
    descartar,
    carregando: permissoesQuery.isLoading,
    aplicarAcesso,
    atualizarAcesso,
    salvarComoNovo,
    salvando,
  };
}
